import { difference, keyBy } from 'es-toolkit';
import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  CircularProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
} from '@mui/material';

import { LineWithEagerlyLoadedStations, Station } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getClientUrls } from '@paragon/login-tools';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { naturalSortByProperty, notNullish } from '@paragon/utilities';

import { loadStationAndComponentDesigns } from '../../store/actions/loadActions';
import { useAppDispatch, useAppSelector } from '../hooks';

export default function SelectStationDialog() {
  const dispatch = useAppDispatch();

  const productionAppUrl = useAppSelector(getClientUrls).productionUrl;

  const [loadingStations, setLoadingStations] = useState(true);
  const [loadingLines, setLoadingLines] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stations, setStations] = useState<readonly Station[]>([]);
  const stationsByGuid = keyBy(stations, (station) => station.guid);
  const [linesWithStations, setLinesWithStations] = useState<readonly LineWithEagerlyLoadedStations[]>([]);

  useEffect(() => {
    designService.stations
      .getStationsForOrganizationShallow()
      .then((stations) => {
        setStations(stations);
        setLoadingStations(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Could not load list of stations.');
        setLoadingStations(false);
      });

    designService.lines
      .getEagerlyLoadedLinesAndDirectoriesForCurrentOrganization()
      .then(({ lines }) => {
        setLinesWithStations(naturalSortByProperty(lines, (lineWithStations) => lineWithStations.line.name));
        setLoadingLines(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Could not load lines.');
        setLoadingLines(false);
      });
  }, [dispatch]);

  const stationGuidsInLines = linesWithStations.flatMap((line) => line.stations.map((lineStation) => lineStation.guid));
  const unassociatedStations = difference(Object.keys(stationsByGuid), stationGuidsInLines);

  const stationSelector =
    linesWithStations.length === 0 ? (
      <StationList stations={stations} />
    ) : (
      <>
        {stations.length === 0 && (
          <Alert severity="info">
            No stations are available. Create a station at <Link href={productionAppUrl}>{productionAppUrl}</Link>.
          </Alert>
        )}
        {linesWithStations.map((lineWithStations) => (
          <Accordion key={lineWithStations.line.guid} defaultExpanded={linesWithStations.length === 1}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{lineWithStations.line.name}</AccordionSummary>
            <AccordionDetails>
              <StationList
                stations={lineWithStations.stations
                  .map((lineStation) => stationsByGuid[lineStation.guid])
                  .filter(notNullish)}
              />
            </AccordionDetails>
          </Accordion>
        ))}
        {unassociatedStations.length > 0 ? (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Stations not in a line</AccordionSummary>
            <AccordionDetails>
              <StationList
                stations={unassociatedStations.map((stationGuid) => stationsByGuid[stationGuid]).filter(notNullish)}
              />
            </AccordionDetails>
          </Accordion>
        ) : null}
      </>
    );

  return (
    <Dialog insistent title="Select Station" variant="design">
      {error ? (
        <InlineNotification type="error">{error}</InlineNotification>
      ) : loadingStations || loadingLines ? (
        <CircularProgress />
      ) : (
        <Stack>{stationSelector}</Stack>
      )}
    </Dialog>
  );
}

interface StationListProps {
  readonly stations: readonly Station[];
}

function StationList({ stations }: StationListProps) {
  const dispatch = useAppDispatch();

  async function onStationSelected(stationGuid: string) {
    dispatch(closeCurrentPopup());
    await dispatch(loadStationAndComponentDesigns(stationGuid));
  }

  return (
    <List>
      {naturalSortByProperty(stations, (station) => station.name).map((station) => (
        <ListItemButton key={station.guid} onClick={() => onStationSelected(station.guid)}>
          <ListItemText primary={station.name} />
        </ListItemButton>
      ))}
    </List>
  );
}
